import React, { useEffect } from "react";
import { CountryDropdown } from "react-country-region-selector";
import { useProductData } from "@/zustand/productStore";
import { useTemplateStore } from "@/zustand/templateStore.js";
import s from "@/components/Inputs/Input.module.scss";
import cn from "@/utils/classNames.js";

/**
 * Countries the active campaign will accept (DEV-3732).
 *
 * Empty means "unknown" - a CRM outage, a cache miss, or a campaign with no
 * list - and CountryDropdown treats an empty whitelist as no filtering at all,
 * so the shopper still sees every country. Konnektive still declines a
 * restricted one and the checkout shows the translated message, so failing
 * open here costs feedback quality rather than enforcement.
 */
const allowedCountriesFor = (allowedCountries) => (Array.isArray(allowedCountries?.base) ? allowedCountries.base : []);

const CountryField = ({ label, value, error, name, handleChange }) => {
  const { country_code, allowed_countries } = useProductData();
  const allowedCountries = allowedCountriesFor(allowed_countries);
  const resolvedValue = value?.toUpperCase() || country_code;
  const isFiltering = allowedCountries.length > 0;
  const isResolvedAllowed = !isFiltering || allowedCountries.includes(resolvedValue);

  // A value the filtered list does not contain matches no <option>, so the
  // select renders blank while form state still holds it - and the order goes
  // out with a country the campaign refuses. Reachable via a campaign default
  // that is off its own list, or email prefill writing straight to form state.
  useEffect(() => {
    if (isFiltering && !isResolvedAllowed) {
      handleChange("");
    }
  }, [isFiltering, isResolvedAllowed]);
  const isEnhancedContrast = useTemplateStore((state) => state.enhancedContrast);

  if (country_code) {
    return (
      <div className={s.inputContainer} style={{ width: "100%" }}>
        <div
          className={cn(
            s.input,
            error && s.input__error,
            isEnhancedContrast && s.enhancedContrast,
            isEnhancedContrast && error && s.enhancedContrast__error
          )}
        >
          {label && (
            <span
              className={cn(
                s.styledLabel,
                s.styledLabel__active,
                isEnhancedContrast && s.styledLabel__enhancedContrast
              )}
            >
              {label}
            </span>
          )}
          <div className={s.input__customSelect}>
            <CountryDropdown
              id="country"
              valueType="short"
              labelType="full"
              value={isResolvedAllowed ? resolvedValue : ""}
              // CountryDropdown filters its options once, in the constructor -
              // it has no componentDidUpdate and no getDerivedStateFromProps,
              // so a whitelist that changes later is silently ignored. Keying
              // on the list forces a remount when the campaign changes.
              key={allowedCountries.join(",")}
              whitelist={allowedCountries}
              classes={cn(s.input__select, s.overflowedSelect, isEnhancedContrast && s.enhancedContrast)}
              autoComplete="country-name"
              name={name}
              onChange={(value) => {
                handleChange(value);
              }}
            />
          </div>
        </div>
        {error && (
          <div className="formError" style={{ marginTop: "4px" }}>
            {error}
          </div>
        )}
      </div>
    );
  }
};

export default CountryField;
